<template>
  <FileImportDialog
    :open="open"
    :account-id="accountId"
    :responsibility-id="responsibilityId"
    :checkpoint-id="checkpointId"
    :start-import="startImport"
    :poll-status="pollStatus"
    :config="config"
    @success="emit('success', $event)"
    @close="emit('close')"
  />
</template>

<script setup>
import FileImportDialog from "@components/FileImportDialog/FileImportDialog.vue";
import {
  formatPdfFileSize,
  getPdfImportErrorMessage,
  getPdfImportJobFailureMessage,
  getPdfImportStartErrorMessage,
  PDF_IMPORT_MULTIPLE_FILES_MESSAGE,
  validatePdfFile,
} from "@components/utils/pdfImport";

/**
 * ER-12394 — PdfImportDialog (Article Checkpoint import from PDF, behind idea-MDP-630).
 * Thin wrapper over FileImportDialog (ER-12400) that supplies the PDF config.
 */
defineProps({
  /** Boolean that controls dialog visibility. */
  open: {
    type: Boolean,
    default: false,
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
  responsibilityId: {
    type: [Number, String],
    default: null,
  },
  checkpointId: {
    type: [Number, String],
    default: null,
  },
  /** POST the multipart import; resolves 202 `{ data: { job_id } }`, rejects 422 with a PDF_IMPORT_* code. */
  startImport: {
    type: Function,
    default: null,
  },
  /** GET the job status; resolves `{ data: { status, progress } }`. */
  pollStatus: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(["success", "close"]);

const config = {
  testId: "pdf-import-dialog",
  accept: ".pdf,application/pdf",
  mimeType: "application/pdf",
  fileKind: "PDF",
  canOpenInViewer: true,
  strings: {
    title: "Import from PDF",
    description: "Choose a PDF to import its text, headings and images into this article.",
    dropzoneHint: "PDF only (max. 25 MB)",
    inputLabel: "Choose a PDF file",
    busyTitle: "Importing from PDF",
    startingDescription: "Checking the file and preparing it for import.",
    importingDescription:
      "Reading the PDF and re-hosting its images. Keep this window open until it finishes.",
    startingStatus: "Reading the PDF…",
    importingStatus: "Re-hosting images",
    failedTitle: "Import failed",
    failedDescription: "Nothing was added to your article.",
  },
  multipleFilesMessage: PDF_IMPORT_MULTIPLE_FILES_MESSAGE,
  formatFileSize: formatPdfFileSize,
  validateFile: validatePdfFile,
  getErrorMessage: getPdfImportErrorMessage,
  getStartErrorMessage: getPdfImportStartErrorMessage,
  getJobFailureMessage: getPdfImportJobFailureMessage,
};
</script>
